import type { Type } from '@angular/core';

import type { ExampleData } from './example-data';

export const EXAMPLE_COMPONENTS: Record<string, (string | Partial<ExampleData>)[]> = {
  accordion: [
    'accordion-showcase',
    'accordion-basic',
    'accordion-nested-lazy',
    { id: 'accordion-wizard', hasStyle: true },
  ],
  'action-group': ['action-group-basic', { id: 'action-group-complex', hasStyle: true }],
  alert: ['alert-showcase', 'alert-basic', 'alert-group'],
  autocomplete: [
    'autocomplete-showcase',
    'autocomplete-basic',
    'autocomplete-hint-optgroup',
    'autocomplete-complex-value',
    'autocomplete-with-actions',
  ],
  badge: ['badge-basic', 'badge-before', 'badge-header-button'],
  breadcrumb: ['breadcrumb-basic', 'breadcrumb-collapsed'],
  button: [
    'button-showcase',
    'button-link-basic',
    'button-static-basic',
    'mini-button-showcase',
    'mini-button-group',
  ],
  calendar: [
    'calendar-showcase',
    'calendar-reactive-form',
    'calendar-with-filter',
    { id: 'calendar-enhanced', hasStyle: true },
    'calendar-fixed-month',
  ],
  card: ['card-basic', 'card-link', 'card-button'],
  carousel: [
    { id: 'carousel-basic', hasStyle: true },
    'carousel-link',
    { id: 'carousel-button', hasStyle: true },
  ],
  checkbox: [
    'checkbox-showcase',
    'checkbox-signal',
    'checkbox-reactive',
    'checkbox-template-driven',
  ],
  'checkbox-group': [
    'checkbox-group-showcase',
    { id: 'checkbox-group-panel-showcase', hasStyle: true },
    'checkbox-group-with-error',
  ],
  'checkbox-panel': [
    { id: 'checkbox-panel-showcase', hasStyle: true },
    { id: 'checkbox-panel-signal', hasStyle: true },
    { id: 'checkbox-panel-reactive', hasStyle: true },
    { id: 'checkbox-panel-template-driven', hasStyle: true },
  ],
  chip: [
    'chip-showcase',
    'chip-autocomplete',
    'chip-complex-value',
    'chip-signal',
    'chip-reactive',
    'chip-template-driven',
  ],
  'chip-label': ['chip-label-showcase'],
  clock: ['clock-basic', 'clock-paused'],
  container: [
    { id: 'container-showcase', hasStyle: true },
    { id: 'container-background-image', hasStyle: true },
    { id: 'container-sticky-bar', hasStyle: true },
    { id: 'container-sticky-bar-control-sticky-state', hasStyle: true },
  ],
  'date-input': [
    'date-input-showcase',
    'date-input-signal',
    'date-input-reactive',
    'date-input-template-driven',
  ],
  datepicker: ['datepicker-showcase', 'datepicker-basic'],
  dialog: [
    'dialog-showcase',
    'dialog-service',
    'dialog-nested',
    'dialog-shared-data',
    'dialog-dimensions',
  ],
  divider: [{ id: 'divider-basic', hasStyle: true }],
  download: ['download-showcase', 'download-custom-content'],
  'easter-egg': ['easter-egg-basic'],
  'expansion-panel': ['expansion-panel-showcase', 'expansion-panel-basic'],
  'file-selector': [
    'file-selector-showcase',
    'file-selector-dropzone-showcase',
    'file-selector-signal',
    'file-selector-reactive-form',
    'file-selector-template-driven',
  ],
  'flip-card': ['flip-card-showcase', 'flip-card-basic', 'flip-card-chip'],
  footer: ['footer-basic', { id: 'footer-clock', hasStyle: true }],
  'form-field': [
    'form-field-showcase',
    'form-field-native-select',
    'form-field-clear',
    'form-field-hint',
    'form-field-text-counter',
    'form-field-prefix-suffix',
  ],
  header: [
    'header-showcase',
    'header-environment',
    { id: 'header-scroll-origin', hasStyle: true },
    'header-usermenu',
  ],
  icon: ['icon-basic'],
  'icon-sidebar': [{ id: 'icon-sidebar-showcase', hasStyle: true }, 'icon-sidebar-nested'],
  image: ['image-basic', 'image-with-chip', 'image-round'],
  'journey-header': ['journey-header-showcase'],
  'lead-container': ['lead-container-basic'],
  link: ['link-showcase', 'block-link-showcase'],
  'link-list': ['link-list-showcase'],
  'link-list-anchor': ['link-list-anchor-showcase'],
  'loading-indicator': [
    'loading-indicator-showcase',
    'loading-indicator-minimum-display-time',
    'loading-indicator-expected-short-loading-time',
    'loading-indicator-expected-long-loading-time',
  ],
  'loading-indicator-circle': [
    'loading-indicator-circle-showcase',
    'loading-indicator-circle-minimum-display-time',
    'loading-indicator-circle-expected-short-loading-time',
    'loading-indicator-circle-expected-long-loading-time',
  ],
  logo: [
    { id: 'logo-showcase', hasStyle: true },
    { id: 'logo-cargo-showcase', hasStyle: true },
    { id: 'logo-cargo-international-showcase', hasStyle: true },
    { id: 'logo-elvetino-showcase', hasStyle: true },
    { id: 'logo-anniversary-showcase', hasStyle: true },
  ],
  'map-container': [{ id: 'map-container-basic', hasStyle: true }],
  menu: [
    'menu-basic',
    { id: 'menu-custom-content', hasStyle: true },
    'menu-nested',
    'menu-without-icons',
  ],
  message: [{ id: 'message-basic', hasStyle: true }],
  'mini-calendar': [
    'mini-calendar-showcase',
    'mini-calendar-with-data-source',
    'mini-calendar-with-tooltip',
    'mini-calendar-with-tooltip-and-data-source',
  ],
  navigation: ['navigation-basic', 'navigation-section'],
  notification: ['notification-showcase', 'notification-static'],
  option: ['option-showcase'],
  overlay: ['overlay-showcase', 'overlay-service', 'overlay-nested'],
  paginator: ['paginator-showcase', 'paginator-compact'],
  'pearl-chain': [
    { id: 'pearl-chain-horizontal-showcase', hasStyle: true },
    { id: 'pearl-chain-vertical-showcase', hasStyle: true },
    { id: 'pearl-chain-full-horizontal', hasStyle: true },
    { id: 'pearl-chain-full-vertical', hasStyle: true },
    { id: 'pearl-chain-weighted-nodes', hasStyle: true },
  ],
  popover: [{ id: 'popover-showcase', hasStyle: true }],
  'radio-button': ['radio-button-showcase'],
  'radio-button-group': [
    'radio-button-group-showcase',
    'radio-button-group-signal',
    'radio-button-group-reactive',
    'radio-button-group-template-driven',
    'radio-button-group-compare-with',
  ],
  'radio-button-panel': [
    { id: 'radio-button-panel-showcase', hasStyle: true },
    { id: 'radio-button-panel-signal', hasStyle: true },
    { id: 'radio-button-panel-reactive', hasStyle: true },
    { id: 'radio-button-panel-template-driven', hasStyle: true },
  ],
  'seat-reservation': [
    'seat-reservation-basic',
    'seat-reservation-multiple-decks',
    'seat-reservation-bus',
  ],
  select: [
    'select-showcase',
    'select-optgroup',
    'select-complex-value',
    'select-compare-with',
    'select-signal',
    'select-reactive',
    'select-template-driven',
  ],
  'selection-action-panel': [
    'selection-action-panel-basic',
    'selection-action-panel-with-expansion',
    'selection-action-panel-group',
  ],
  'selection-expansion-panel': [
    { id: 'selection-expansion-panel-showcase', hasStyle: true },
    { id: 'selection-expansion-panel-group', hasStyle: true },
    { id: 'selection-expansion-panel-ticket-options', hasStyle: true },
  ],
  sidebar: [{ id: 'sidebar-showcase', hasStyle: true }, 'sidebar-nested'],
  signet: [{ id: 'signet-showcase', hasStyle: true }],
  'skiplink-list': ['skiplink-list-basic'],
  slider: ['slider-showcase', 'slider-signal', 'slider-reactive', 'slider-template-driven'],
  status: ['status-showcase'],
  stepper: ['stepper-showcase', 'stepper-lazy-loaded'],
  table: [
    'table-simple',
    { id: 'table-sticky', hasStyle: true },
    'table-grouped-columns',
    'table-grouped-rows-and-columns',
    'table-sortable',
    { id: 'table-paginator', hasStyle: true },
    { id: 'table-selectable', hasStyle: true },
    { id: 'table-filter-sort-paginator', hasStyle: true },
    { id: 'table-native', hasStyle: true },
    { id: 'table-expandable', hasStyle: true },
  ],
  tabs: [
    'tabs-showcase',
    { id: 'tabs-fixed-height', hasStyle: true },
    'tabs-tab-nav-bar',
    'tabs-lazy-content',
  ],
  tag: [
    'tag-showcase',
    'tag-exclusive',
    { id: 'tag-all-filter', hasStyle: true },
    'tag-signal',
    'tag-reactive',
    'tag-template-driven',
    'tag-signal-exclusive',
  ],
  teaser: [
    'teaser-showcase',
    { id: 'teaser-list', hasStyle: true },
    { id: 'teaser-grid', hasStyle: true },
    'teaser-with-button',
    'teaser-static',
  ],
  'teaser-hero': ['teaser-hero-basic', 'teaser-hero-with-chip', 'teaser-hero-chip-only'],
  'teaser-panel': [{ id: 'teaser-panel-basic', hasStyle: true }],
  'teaser-product': ['teaser-product-showcase', 'teaser-product-static', 'teaser-product-chip'],
  'time-input': [
    'time-input-showcase',
    'time-input-signal',
    'time-input-reactive',
    'time-input-template-driven',
  ],
  'timetable-form': [
    { id: 'timetable-form-basic', hasStyle: true },
    { id: 'timetable-form-via', hasStyle: true },
  ],
  'timetable-occupancy': ['timetable-occupancy-showcase'],
  'timetable-occupancy-icon': ['timetable-occupancy-icon-showcase'],
  title: ['title-showcase'],
  toast: ['toast-showcase', 'toast-service'],
  toggle: ['toggle-showcase', 'toggle-signal', 'toggle-reactive', 'toggle-template-driven'],
  'toggle-check': [
    'toggle-check-showcase',
    'toggle-check-signal',
    'toggle-check-reactive',
    'toggle-check-template-driven',
  ],
  tooltip: [{ id: 'tooltip-showcase', hasStyle: true }, 'tooltip-basic', 'tooltip-attribute'],
  train: ['train-showcase', 'train-wagon-showcase', 'train-vertical'],
};

export async function loadExample(id: string): Promise<Record<string, Type<unknown>> | undefined> {
  switch (id) {
    case 'easter-egg-basic':
      return import('../angular-experimental/examples/easter-egg');
    case 'seat-reservation-basic':
    case 'seat-reservation-bus':
    case 'seat-reservation-multiple-decks':
      return import('../angular-experimental/examples/seat-reservation');
    case 'accordion-basic':
    case 'accordion-nested-lazy':
    case 'accordion-showcase':
    case 'accordion-wizard':
      return import('../angular/examples/accordion');
    case 'action-group-basic':
    case 'action-group-complex':
      return import('../angular/examples/action-group');
    case 'alert-basic':
    case 'alert-group':
    case 'alert-showcase':
      return import('../angular/examples/alert');
    case 'autocomplete-basic':
    case 'autocomplete-complex-value':
    case 'autocomplete-hint-optgroup':
    case 'autocomplete-showcase':
    case 'autocomplete-with-actions':
      return import('../angular/examples/autocomplete');
    case 'badge-basic':
    case 'badge-before':
    case 'badge-header-button':
      return import('../angular/examples/badge');
    case 'breadcrumb-basic':
    case 'breadcrumb-collapsed':
      return import('../angular/examples/breadcrumb');
    case 'button-link-basic':
    case 'button-showcase':
    case 'button-static-basic':
    case 'mini-button-group':
    case 'mini-button-showcase':
      return import('../angular/examples/button');
    case 'calendar-enhanced':
    case 'calendar-fixed-month':
    case 'calendar-reactive-form':
    case 'calendar-showcase':
    case 'calendar-with-filter':
      return import('../angular/examples/calendar');
    case 'card-basic':
    case 'card-button':
    case 'card-link':
      return import('../angular/examples/card');
    case 'carousel-basic':
    case 'carousel-button':
    case 'carousel-link':
      return import('../angular/examples/carousel');
    case 'checkbox-reactive':
    case 'checkbox-showcase':
    case 'checkbox-signal':
    case 'checkbox-template-driven':
      return import('../angular/examples/checkbox');
    case 'checkbox-group-panel-showcase':
    case 'checkbox-group-showcase':
    case 'checkbox-group-with-error':
      return import('../angular/examples/checkbox-group');
    case 'checkbox-panel-reactive':
    case 'checkbox-panel-showcase':
    case 'checkbox-panel-signal':
    case 'checkbox-panel-template-driven':
      return import('../angular/examples/checkbox-panel');
    case 'chip-autocomplete':
    case 'chip-complex-value':
    case 'chip-reactive':
    case 'chip-showcase':
    case 'chip-signal':
    case 'chip-template-driven':
      return import('../angular/examples/chip');
    case 'chip-label-showcase':
      return import('../angular/examples/chip-label');
    case 'clock-basic':
    case 'clock-paused':
      return import('../angular/examples/clock');
    case 'container-background-image':
    case 'container-showcase':
    case 'container-sticky-bar':
    case 'container-sticky-bar-control-sticky-state':
      return import('../angular/examples/container');
    case 'date-input-reactive':
    case 'date-input-showcase':
    case 'date-input-signal':
    case 'date-input-template-driven':
      return import('../angular/examples/date-input');
    case 'datepicker-basic':
    case 'datepicker-showcase':
      return import('../angular/examples/datepicker');
    case 'dialog-dimensions':
    case 'dialog-nested':
    case 'dialog-service':
    case 'dialog-shared-data':
    case 'dialog-showcase':
      return import('../angular/examples/dialog');
    case 'divider-basic':
      return import('../angular/examples/divider');
    case 'download-custom-content':
    case 'download-showcase':
      return import('../angular/examples/download');
    case 'expansion-panel-basic':
    case 'expansion-panel-showcase':
      return import('../angular/examples/expansion-panel');
    case 'file-selector-dropzone-showcase':
    case 'file-selector-reactive-form':
    case 'file-selector-showcase':
    case 'file-selector-signal':
    case 'file-selector-template-driven':
      return import('../angular/examples/file-selector');
    case 'flip-card-basic':
    case 'flip-card-chip':
    case 'flip-card-showcase':
      return import('../angular/examples/flip-card');
    case 'footer-basic':
    case 'footer-clock':
      return import('../angular/examples/footer');
    case 'form-field-clear':
    case 'form-field-hint':
    case 'form-field-native-select':
    case 'form-field-prefix-suffix':
    case 'form-field-showcase':
    case 'form-field-text-counter':
      return import('../angular/examples/form-field');
    case 'header-environment':
    case 'header-scroll-origin':
    case 'header-showcase':
    case 'header-usermenu':
      return import('../angular/examples/header');
    case 'icon-basic':
      return import('../angular/examples/icon');
    case 'icon-sidebar-nested':
    case 'icon-sidebar-showcase':
      return import('../angular/examples/icon-sidebar');
    case 'image-basic':
    case 'image-round':
    case 'image-with-chip':
      return import('../angular/examples/image');
    case 'journey-header-showcase':
      return import('../angular/examples/journey-header');
    case 'lead-container-basic':
      return import('../angular/examples/lead-container');
    case 'block-link-showcase':
    case 'link-showcase':
      return import('../angular/examples/link');
    case 'link-list-showcase':
      return import('../angular/examples/link-list');
    case 'link-list-anchor-showcase':
      return import('../angular/examples/link-list-anchor');
    case 'loading-indicator-expected-long-loading-time':
    case 'loading-indicator-expected-short-loading-time':
    case 'loading-indicator-minimum-display-time':
    case 'loading-indicator-showcase':
      return import('../angular/examples/loading-indicator');
    case 'loading-indicator-circle-expected-long-loading-time':
    case 'loading-indicator-circle-expected-short-loading-time':
    case 'loading-indicator-circle-minimum-display-time':
    case 'loading-indicator-circle-showcase':
      return import('../angular/examples/loading-indicator-circle');
    case 'logo-anniversary-showcase':
    case 'logo-cargo-international-showcase':
    case 'logo-cargo-showcase':
    case 'logo-elvetino-showcase':
    case 'logo-showcase':
      return import('../angular/examples/logo');
    case 'map-container-basic':
      return import('../angular/examples/map-container');
    case 'menu-basic':
    case 'menu-custom-content':
    case 'menu-nested':
    case 'menu-without-icons':
      return import('../angular/examples/menu');
    case 'message-basic':
      return import('../angular/examples/message');
    case 'mini-calendar-showcase':
    case 'mini-calendar-with-data-source':
    case 'mini-calendar-with-tooltip':
    case 'mini-calendar-with-tooltip-and-data-source':
      return import('../angular/examples/mini-calendar');
    case 'navigation-basic':
    case 'navigation-section':
      return import('../angular/examples/navigation');
    case 'notification-showcase':
    case 'notification-static':
      return import('../angular/examples/notification');
    case 'option-showcase':
      return import('../angular/examples/option');
    case 'overlay-nested':
    case 'overlay-service':
    case 'overlay-showcase':
      return import('../angular/examples/overlay');
    case 'paginator-compact':
    case 'paginator-showcase':
      return import('../angular/examples/paginator');
    case 'pearl-chain-full-horizontal':
    case 'pearl-chain-full-vertical':
    case 'pearl-chain-horizontal-showcase':
    case 'pearl-chain-vertical-showcase':
    case 'pearl-chain-weighted-nodes':
      return import('../angular/examples/pearl-chain');
    case 'popover-showcase':
      return import('../angular/examples/popover');
    case 'radio-button-showcase':
      return import('../angular/examples/radio-button');
    case 'radio-button-group-compare-with':
    case 'radio-button-group-reactive':
    case 'radio-button-group-showcase':
    case 'radio-button-group-signal':
    case 'radio-button-group-template-driven':
      return import('../angular/examples/radio-button-group');
    case 'radio-button-panel-reactive':
    case 'radio-button-panel-showcase':
    case 'radio-button-panel-signal':
    case 'radio-button-panel-template-driven':
      return import('../angular/examples/radio-button-panel');
    case 'select-compare-with':
    case 'select-complex-value':
    case 'select-optgroup':
    case 'select-reactive':
    case 'select-showcase':
    case 'select-signal':
    case 'select-template-driven':
      return import('../angular/examples/select');
    case 'selection-action-panel-basic':
    case 'selection-action-panel-group':
    case 'selection-action-panel-with-expansion':
      return import('../angular/examples/selection-action-panel');
    case 'selection-expansion-panel-group':
    case 'selection-expansion-panel-showcase':
    case 'selection-expansion-panel-ticket-options':
      return import('../angular/examples/selection-expansion-panel');
    case 'sidebar-nested':
    case 'sidebar-showcase':
      return import('../angular/examples/sidebar');
    case 'signet-showcase':
      return import('../angular/examples/signet');
    case 'skiplink-list-basic':
      return import('../angular/examples/skiplink-list');
    case 'slider-reactive':
    case 'slider-showcase':
    case 'slider-signal':
    case 'slider-template-driven':
      return import('../angular/examples/slider');
    case 'status-showcase':
      return import('../angular/examples/status');
    case 'stepper-lazy-loaded':
    case 'stepper-showcase':
      return import('../angular/examples/stepper');
    case 'table-expandable':
    case 'table-filter-sort-paginator':
    case 'table-grouped-columns':
    case 'table-grouped-rows-and-columns':
    case 'table-native':
    case 'table-paginator':
    case 'table-selectable':
    case 'table-simple':
    case 'table-sortable':
    case 'table-sticky':
      return import('../angular/examples/table');
    case 'tabs-fixed-height':
    case 'tabs-lazy-content':
    case 'tabs-showcase':
    case 'tabs-tab-nav-bar':
      return import('../angular/examples/tabs');
    case 'tag-all-filter':
    case 'tag-exclusive':
    case 'tag-reactive':
    case 'tag-showcase':
    case 'tag-signal':
    case 'tag-signal-exclusive':
    case 'tag-template-driven':
      return import('../angular/examples/tag');
    case 'teaser-grid':
    case 'teaser-list':
    case 'teaser-showcase':
    case 'teaser-static':
    case 'teaser-with-button':
      return import('../angular/examples/teaser');
    case 'teaser-hero-basic':
    case 'teaser-hero-chip-only':
    case 'teaser-hero-with-chip':
      return import('../angular/examples/teaser-hero');
    case 'teaser-panel-basic':
      return import('../angular/examples/teaser-panel');
    case 'teaser-product-chip':
    case 'teaser-product-showcase':
    case 'teaser-product-static':
      return import('../angular/examples/teaser-product');
    case 'time-input-reactive':
    case 'time-input-showcase':
    case 'time-input-signal':
    case 'time-input-template-driven':
      return import('../angular/examples/time-input');
    case 'timetable-form-basic':
    case 'timetable-form-via':
      return import('../angular/examples/timetable-form');
    case 'timetable-occupancy-showcase':
      return import('../angular/examples/timetable-occupancy');
    case 'timetable-occupancy-icon-showcase':
      return import('../angular/examples/timetable-occupancy-icon');
    case 'title-showcase':
      return import('../angular/examples/title');
    case 'toast-service':
    case 'toast-showcase':
      return import('../angular/examples/toast');
    case 'toggle-reactive':
    case 'toggle-showcase':
    case 'toggle-signal':
    case 'toggle-template-driven':
      return import('../angular/examples/toggle');
    case 'toggle-check-reactive':
    case 'toggle-check-showcase':
    case 'toggle-check-signal':
    case 'toggle-check-template-driven':
      return import('../angular/examples/toggle-check');
    case 'tooltip-attribute':
    case 'tooltip-basic':
    case 'tooltip-showcase':
      return import('../angular/examples/tooltip');
    case 'train-showcase':
    case 'train-vertical':
    case 'train-wagon-showcase':
      return import('../angular/examples/train');
    default:
      console.warn(`No example found for component with id "${id}".`);
      return undefined;
  }
}
